import { useEffect, useMemo, useState } from 'react';
import { api, getToken } from './api';
import {
  hostLabel,
  useActiveHost,
  useActiveLayout,
  useActiveWorkspace,
  useStore,
  useWorkspaceSessions,
} from './store';
import { connection } from './ws';
import { LoginScreen } from './components/LoginScreen';
import { Sidebar } from './components/Sidebar';
import { SessionPane } from './components/SessionPane';
import { NewSessionDialog } from './components/NewSessionDialog';
import { PromptPanel } from './components/PromptPanel';
import { ProfilePanel } from './components/ProfilePanel';
import { AppearancePanel } from './components/AppearancePanel';
import { WorkspaceTabs } from './components/WorkspaceTabs';
import { WorkspaceDialog } from './components/WorkspaceDialog';
import { BrandMark } from './components/BrandMark';
import { UpdateBanner } from './components/UpdateBanner';
import { PaneGrid } from './components/PaneGrid';
import { MinimizedStrip } from './components/MinimizedStrip';
import { MobileSwitcher } from './components/MobileSwitcher';
import { MAX_COLUMNS } from './layout';
import { useNarrow, useVisualViewportBox } from './viewport';
import { chimeEnabled, installChime, setChimeEnabled } from './chime';

export function App() {
  const user = useStore((s) => s.user);
  const connected = useStore((s) => s.connected);
  const focusedId = useStore((s) => s.focusedId);
  const layout = useStore((s) => s.layout);
  const setLayout = useStore((s) => s.setLayout);
  const panel = useStore((s) => s.panel);
  const openPanel = useStore((s) => s.openPanel);
  const toasts = useStore((s) => s.toasts);
  const dismissToast = useStore((s) => s.dismissToast);
  const setUser = useStore((s) => s.setUser);
  const logout = useStore((s) => s.logout);
  const refreshAll = useStore((s) => s.refreshAll);
  const syncLayout = useStore((s) => s.syncLayout);
  const updateLayout = useStore((s) => s.updateLayout);
  const layoutState = useActiveLayout();
  const sidebarPinned = useStore((s) => s.sidebarPinned);
  const initHosts = useStore((s) => s.initHosts);
  const hosts = useStore((s) => s.hosts);
  const activeHostId = useStore((s) => s.activeHostId);
  const switchHost = useStore((s) => s.switchHost);
  const activeHost = useActiveHost();

  const workspace = useActiveWorkspace();
  const sessions = useWorkspaceSessions();

  const [checkingSession, setChecking] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [workspaceDialog, setWorkspaceDialog] = useState<'new' | 'edit' | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [chime, setChime] = useState(chimeEnabled);
  const narrow = useNarrow();
  useVisualViewportBox();

  // O menu do celular não tem o que fazer numa janela larga.
  useEffect(() => {
    if (!narrow) setDrawer(false);
  }, [narrow]);

  // Carrega a lista de hosts antes de qualquer requisicao: e ela que define
  // para onde a camada de API vai apontar.
  useEffect(() => {
    initHosts();
  }, [initHosts]);

  // Valida o token guardado a cada troca de host, para o refresh da pagina (e
  // a troca de servidor) nao derrubar uma sessao que ainda e valida.
  useEffect(() => {
    if (!activeHostId) {
      setChecking(false);
      return;
    }
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    api
      .me()
      .then((res) => {
        if (!cancelled) setUser(res.user);
      })
      .catch(() => {
        if (!cancelled) logout('o acesso a este servidor expirou');
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeHostId, setUser, logout]);

  // Depende do id, não do objeto: reatar a cada nova referência de `user`
  // religaria o socket sem necessidade.
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) return;
    const token = getToken();
    if (!token) return;
    connection.connect(token);
    void refreshAll();
    const removeChime = installChime();
    return () => {
      removeChime();
      connection.disconnect();
    };
  }, [userId, refreshAll]);

  // O layout guarda ids; as sessoes mudam. Reconciliar aqui mantem a ordem e
  // os minimizados coerentes com o que existe de fato.
  const sessionIds = useMemo(() => sessions.map((s) => s.id).join(','), [sessions]);
  useEffect(() => {
    if (!workspace) return;
    syncLayout(workspace.id, sessionIds ? sessionIds.split(',') : []);
  }, [workspace, sessionIds, syncLayout]);

  if (checkingSession) {
    return (
      <div className="login-screen">
        <p className="empty-hint">conectando…</p>
      </div>
    );
  }

  if (!user) return <LoginScreen />;

  return (
    <div className={`app${narrow ? ' is-narrow' : ''}`}>
      <header className="topbar">
        <button
          type="button"
          className="menu-btn"
          aria-expanded={drawer}
          aria-label={drawer ? 'Fechar menu' : 'Abrir menu'}
          onClick={() => setDrawer((open) => !open)}
        >
          ☰
        </button>
        <div className="topbar-brand">
          <BrandMark size={16} />
          <span className="brand-name">Code Manager</span>
        </div>

        {/* Seletor de servidor: o app conecta em qualquer daemon, e trocar
            aqui recarrega projetos, sessoes e perfis daquele host. */}
        {hosts.length > 1 && (
          <select
            className="host-select"
            value={activeHostId ?? ''}
            onChange={(e) => switchHost(e.target.value)}
            title={activeHost?.url || 'mesma origem'}
          >
            {hosts.map((h) => (
              <option key={h.id} value={h.id}>
                {hostLabel(h)}
              </option>
            ))}
          </select>
        )}

        <WorkspaceTabs onCreate={() => setWorkspaceDialog('new')} />

        <div className="topbar-actions">
          <UpdateBanner />
          <button
            className="primary"
            onClick={() => setShowNewSession(true)}
            disabled={!workspace}
            title={workspace ? 'Abrir um agente neste projeto' : 'Crie um projeto primeiro'}
          >
            + Agente
          </button>
          <button
            className={panel === 'prompts' ? 'primary' : ''}
            onClick={() => openPanel('prompts')}
          >
            Prompts
          </button>
          <button
            className={panel === 'profiles' ? 'primary' : ''}
            onClick={() => openPanel('profiles')}
          >
            Perfis
          </button>
          <button
            className={panel === 'appearance' ? 'primary' : ''}
            onClick={() => openPanel('appearance')}
            title="Cores do sistema e identidade dos panes"
          >
            Aparência
          </button>
          {/* Colunas: "auto" segue a quantidade de panes; um numero fixa a
              grade, que e o que permite "um do lado do outro" ou "um embaixo
              do outro" de forma previsivel. */}
          <select
            className="col-select"
            value={layoutState.columns}
            onChange={(e) =>
              workspace &&
              updateLayout(workspace.id, (l) => ({ ...l, columns: Number(e.target.value) }))
            }
            disabled={!workspace}
            title="Quantas colunas o mosaico usa"
          >
            <option value={0}>auto</option>
            {Array.from({ length: MAX_COLUMNS }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? 'coluna' : 'colunas'}
              </option>
            ))}
          </select>

          <button
            className={`ghost chime${chime ? ' on' : ''}`}
            aria-pressed={chime}
            title={
              chime
                ? 'Aviso sonoro ligado. Toca quando um agente termina a resposta. A guia precisa continuar aberta.'
                : 'Ligar o aviso sonoro quando um agente termina a resposta'
            }
            onClick={() => {
              const next = !chime;
              setChimeEnabled(next);
              setChime(next);
            }}
          >
            ♪
          </button>
          <span className="conn" title={connected ? 'conectado ao daemon' : 'reconectando'}>
            <span className={`dot${connected ? ' on' : ''}`} />
            {user.username}
          </span>
          <button className="ghost" onClick={() => logout()} title="Sair">
            ⏏
          </button>
        </div>
      </header>

      <div
        className={`body${panel ? ' with-panel' : ''}${sidebarPinned ? ' sidebar-pinned' : ''}`}
      >
        <Sidebar
          onEditWorkspace={() => setWorkspaceDialog('edit')}
          allowHover={!narrow}
          forcedOpen={drawer}
          onNavigated={() => setDrawer(false)}
        />
        {narrow && drawer && (
          <button
            type="button"
            className="drawer-backdrop"
            aria-label="Fechar menu"
            onClick={() => setDrawer(false)}
          />
        )}

        <main className="pane-area">
          {!workspace ? (
            <p className="empty-hint">
              Nenhum projeto ainda. Clique no <strong>+</strong> ao lado das abas para criar um
              workspace apontando para a pasta do seu projeto.
            </p>
          ) : sessions.length === 0 ? (
            <p className="empty-hint">
              <strong>{workspace.name}</strong> está sem agentes. Use <strong>+ Agente</strong> para
              abrir um Claude Code, Codex ou Gemini nesta pasta — vários podem trabalhar juntos,
              cada um no seu pane.
            </p>
          ) : (
            <>
              {narrow && <MobileSwitcher sessions={sessions} />}
              <PaneGrid workspaceId={workspace.id} sessions={sessions} single={narrow} />
              <MinimizedStrip workspaceId={workspace.id} sessions={sessions} />
            </>
          )}
        </main>

        {panel === 'prompts' && <PromptPanel />}
        {panel === 'profiles' && <ProfilePanel />}
        {panel === 'appearance' && <AppearancePanel />}
      </div>

      {showNewSession && workspace && (
        <NewSessionDialog workspace={workspace} onClose={() => setShowNewSession(false)} />
      )}

      {workspaceDialog && (
        <WorkspaceDialog
          workspace={workspaceDialog === 'edit' ? (workspace ?? undefined) : undefined}
          onClose={() => setWorkspaceDialog(null)}
        />
      )}

      <div className="toasts">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast ${toast.kind}`}
            onClick={() => dismissToast(toast.id)}
          >
            {toast.text}
          </div>
        ))}
      </div>
    </div>
  );
}
